import React from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import Spinner from '../Spinner/Spinner'

export default function Button({
  use: Component = 'button',
  outline = false,
  glow = !outline,
  color = 'green',
  block = false,
  big = false,
  loading = false,
  children,
  fontWeight,
  height,
  tracking,
  className,
  ...props
}) {
  const glowElement = (
    <span
      className={classNames('absolute', {
        'bg-gradient-green': color === 'green',
        'bg-gradient-premium': color === 'premium',
      })}
      style={{
        top: '4px',
        height: '110%',
        width: '85%',
        left: '7.5%',
        filter: 'blur(11px)',
        opacity: 0.3,
      }}
    />
  )

  return (
    <Component
      className={classNames(
        className,
        'relative no-underline max-w-full',
        {
          'inline-block': !block,
          'block w-full': block,
        },
        'relative',
        'rounded-full',
        'items-center',
        'justify-center',
        'px-32',
        'hover:translate-y-3px',
        'transition',
        fontWeight || 'font-demibold',
        'text-16',
        'uppercase',
        tracking || 'tracking-1',
        'leading-normal',
        'whitespace-no-wrap',
        'cursor-pointer',
        height || (big ? 'h-60' : 'h-40'),
        {
          'inline-flex': !block,
          flex: block,
          'bg-gradient-green text-white': !outline && color === 'green',
          'border-2 border-green text-green': outline && color === 'green',
          'bg-gradient-premium text-white': !outline && color === 'premium',
          'border-2 border-red text-red': outline && color === 'premium',
          'bg-black text-white': !outline && color === 'black',
          'border-2 border-black text-black': outline && color === 'black',
          'bg-secondary-red text-white': !outline && color === 'red',
          'border-2 border-secondary-red text-secondary-red':
            outline && color === 'red',
          'bg-white text-grey-darkest': !outline && color === 'white',
          'border-2 border-white text-white': outline && color === 'white',
          'opacity-40': props.disabled,
        }
      )}
      {...props}
    >
      {glow && glowElement}
      <span
        className={classNames('truncate transition relative', {
          'opacity-0': loading,
        })}
      >
        {children}
      </span>
      {loading && <Spinner className="absolute" width="26" height="26" />}
    </Component>
  )
}

Button.propTypes = {
  /** Component/element to use for the button */
  use: PropTypes.any,
  outline: PropTypes.bool,
  glow: PropTypes.bool,
  block: PropTypes.bool,
  color: PropTypes.oneOf(['green', 'premium', 'black', 'red', 'white']),
  big: PropTypes.bool,
  loading: PropTypes.bool,
  children: PropTypes.node,
  /** A tailwind class for font-weight, e.g. font-bold */
  fontWeight: PropTypes.string,
  /** A tailwind class for tracking, e.g. tracking-2 */
  tracking: PropTypes.string,
  /** A tailwind class for height, e.g. h-40 */
  height: PropTypes.string,
  /** Any additional classes */
  className: PropTypes.string,
  disabled: PropTypes.bool,
}
